import { useCallback, useRef, useState } from 'react'
import { ROTULO_STATUS_NEGOCIACAO, type NegociacaoDto, type UsuarioSessao } from '@crm/shared'
import { useEquipe } from '../../api/hooks'
import {
  useCampos,
  useFunis,
  useHistoricoDoContato,
  useNovaNegociacao,
  useTags,
} from '../../api/hooks-leads'
import { useFecharAoClicarFora } from '../../componentes/fechar-fora'
import { Flutuante } from '../../componentes/Flutuante'
import { IconeNegociacao, IconeSetaBaixo } from '../../componentes/icones'
import { usePerguntar } from '../../componentes/perguntar'
import { CLASSE_DO_ROTULO } from '../../componentes/pagina'
import { Etiqueta, juntar } from '../../componentes/ui'
import { formatarData, formatarMoeda } from '../../formato'
import { pode } from '../../permissao'
import { ModalContato } from '../leads/ModalContato'

/**
 * A negociação do cliente, no cartão ao lado da conversa (pedido de
 * 06/10/2026: "funcionará como o campo de empresa, só que para negociação").
 *
 * O campo mostra a negociação aberta. Clicando, a lista traz as abertas em
 * cima e as antigas embaixo, com ganho ou perda; escolher uma abre no meio da
 * tela a mesma janela do quadro de Leads, com os dados, o valor e o
 * histórico. No pé, "Nova negociação" abre outro ciclo para o mesmo cliente.
 *
 * Antes o cartão dizia ter negociações, e não tinha: para saber em que etapa
 * o cliente estava era preciso sair da conversa e procurar o card em Leads.
 */
export function NegociacaoDoContato({
  contatoId,
  contatoNome,
  usuario,
}: {
  contatoId: string
  contatoNome: string
  usuario: UsuarioSessao
}) {
  const [aberto, setAberto] = useState(false)
  const [abrindo, setAbrindo] = useState<NegociacaoDto | null>(null)
  const fechar = useCallback(() => setAberto(false), [])
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, fechar)
  const botao = useRef<HTMLButtonElement>(null)
  const { pedirConfirmacao, dialogo } = usePerguntar()

  const { data: historico } = useHistoricoDoContato(contatoId)
  const { data: funis } = useFunis()
  const { data: campos } = useCampos()
  const { data: tags } = useTags()
  const { data: usuarios } = useEquipe()
  const nova = useNovaNegociacao()

  const todas = historico?.itens ?? []
  const abertas = todas
    .filter((n) => n.status === 'aberta')
    .sort((a, b) => b.abertaEm.localeCompare(a.abertaEm))
  const antigas = todas
    .filter((n) => n.status !== 'aberta')
    .sort((a, b) => (b.fechadaEm ?? b.abertaEm).localeCompare(a.fechadaEm ?? a.abertaEm))
  const principal = abertas[0] ?? null
  const funisAtivos = (funis ?? []).filter((f) => f.ativo)
  const podeCriar = pode(usuario, 'leads.editar') && funisAtivos.length > 0

  async function abrirNova() {
    fechar()
    const destino = funisAtivos[0]
    if (!destino) return
    const ok = await pedirConfirmacao(
      `Abrir uma nova negociação para ${contatoNome} no funil ${destino.nome}? Ela entra na primeira etapa, e as anteriores continuam na lista.`,
      { titulo: 'Nova negociação', rotuloConfirmar: 'Abrir', perigo: false },
    )
    if (ok) nova.mutate({ contatoId, funilId: destino.id })
  }

  function linha(n: NegociacaoDto) {
    return (
      <li key={n.id}>
        <button
          type="button"
          onClick={() => {
            fechar()
            setAbrindo(n)
          }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-tinta-50"
        >
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: n.etapaCor }}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-tinta-900">
              {n.funilNome} · {n.etapaNome}
            </span>
            <span className="block truncate text-xs text-tinta-400">
              {n.status === 'aberta'
                ? `aberta em ${formatarData(n.abertaEm)}`
                : `${ROTULO_STATUS_NEGOCIACAO[n.status].toLowerCase()} em ${formatarData(n.fechadaEm ?? n.abertaEm)}`}
            </span>
          </span>
          <span className="shrink-0 text-xs tabular-nums text-tinta-600">
            {n.valor != null ? formatarMoeda(n.valor) : '-'}
          </span>
          {n.status !== 'aberta' ? (
            <Etiqueta cor={n.status === 'ganha' ? 'verde' : 'vermelha'}>
              {ROTULO_STATUS_NEGOCIACAO[n.status]}
            </Etiqueta>
          ) : null}
        </button>
      </li>
    )
  }

  return (
    <div ref={caixa} className="relative w-full">
      <button
        ref={botao}
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className={juntar(
          'flex h-9 w-full items-center gap-2 rounded-md bg-superficie px-2.5 text-left text-sm ring-1 transition hover:ring-tinta-300',
          aberto ? 'ring-2 ring-marca' : 'ring-tinta-200',
        )}
      >
        <IconeNegociacao className="h-4 w-4 shrink-0 text-tinta-400" />
        <span
          className={juntar(
            'min-w-0 flex-1 truncate',
            principal ? 'text-tinta-900' : 'text-tinta-400',
          )}
        >
          {nova.isPending
            ? 'Abrindo…'
            : principal
              ? `${principal.funilNome} · ${principal.etapaNome}`
              : 'Sem negociação aberta'}
        </span>
        {principal?.valor != null ? (
          <span className="shrink-0 text-xs tabular-nums text-tinta-500">
            {formatarMoeda(principal.valor)}
          </span>
        ) : null}
        {abertas.length > 1 ? (
          <span className="shrink-0 text-xs text-tinta-400">+{abertas.length - 1}</span>
        ) : null}
        <IconeSetaBaixo
          className={juntar('h-4 w-4 shrink-0 text-tinta-400 transition', aberto && 'rotate-180')}
        />
      </button>

      {/* Flutua fora da ficha, que rola por dentro e cortaria a lista. */}
      <Flutuante
        ancora={botao}
        aberto={aberto}
        aoFechar={fechar}
        larguraMinima={280}
        alturaMaxima={360}
        className="flex flex-col overflow-hidden rounded-xl bg-superficie shadow-media ring-1 ring-tinta-200"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <ul className="min-h-0 flex-1 overflow-y-auto py-1 text-sm">
            {abertas.length > 0 ? (
              <li className={juntar(CLASSE_DO_ROTULO, 'px-3 pb-1 pt-1.5 text-[11px]')}>Abertas</li>
            ) : null}
            {abertas.map(linha)}
            {antigas.length > 0 ? (
              <li className={juntar(CLASSE_DO_ROTULO, 'px-3 pb-1 pt-2 text-[11px]')}>Antigas</li>
            ) : null}
            {antigas.map(linha)}
            {todas.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-tinta-400">
                Este cliente ainda não teve negociação.
              </li>
            ) : null}
          </ul>
          {podeCriar ? (
            <button
              type="button"
              onClick={() => void abrirNova()}
              className="flex w-full items-center gap-2.5 border-t border-tinta-100 px-3 py-2 text-left text-sm text-marca hover:bg-tinta-50"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-dashed border-marca/50 text-base leading-none">
                +
              </span>
              <span className="font-medium">Nova negociação</span>
            </button>
          ) : null}
        </div>
      </Flutuante>

      <ModalContato
        aberto={abrindo !== null}
        contato={null}
        contatoId={abrindo?.contatoId ?? null}
        negociacao={abrindo}
        campos={campos ?? []}
        tags={tags ?? []}
        usuarios={usuarios ?? []}
        funis={funisAtivos}
        funilPadrao={abrindo?.funilId ?? null}
        aoFechar={() => setAbrindo(null)}
      />
      {dialogo}
    </div>
  )
}
